import { useEffect, useMemo, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE, UserUnitSettings } from '../../common/constants'
import { ChartLoadingPlaceholder } from './ChartLoadingPlaceholder'
import { CollapsibleSection } from '../CollapsibleSection'
import { formatNumber } from '../../common/utils'
import { FlowMeterChartSubHeader } from '../FlowMeterChartSubHeader'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import { convertBaseToDisplay } from '../../common/conversions'
import { Viewport } from '../../common/types'
import { useSensorConfigs } from '../../hooks/useSensorConfigs'
import { createAxisLabelFormatter, alertMarkersToStripLines, AlertMarker, getHiddenRangeStripLines } from '../../utils/chartHelpers'

const { CanvasJSChart } = CanvasJSReact

function getUnitAbbreviation(
  conversions: any,
  unitId: number | string,
  fallback: string = ''
): string {
  if (!conversions || typeof conversions !== 'object') {
    console.warn('⚠️ Invalid conversions object:', conversions)
    return fallback
  }

  // Try both string and number keys
  const abbreviation = conversions[unitId] || conversions[String(unitId)] || conversions[Number(unitId)]

  if (!abbreviation) {
    console.warn(`⚠️ No abbreviation found for unitId ${unitId}`, {
      availableUnits: Object.keys(conversions),
      requestedUnit: unitId,
      requestedType: typeof unitId
    })
    return fallback
  }

  return String(abbreviation)
}

const UNIT_ID_ACRE_FOOT = 3 // Acre-Foot
// const DEFAULT_VOLUME_RATE_UNITS = 6 // Gallon per minute

export interface FlowMeterSettings {
  volume: {
    unitId: number
    precision: number
  }
  volumeRate: {
    unitId: number
    precision: number
  }
  totalVolume: {
    unitId: number
    precision: number
  }
}

interface DataPoints {
  volume: { x: number; y?: number }[]
  totals: { x: number; y?: number }[]
  rate: { x: number; y?: number }[]
}

interface Props {
  device: DeviceInfo
  sigfoxId: string
  isLoading: boolean
  sensorPort: string
  sensorProduct: SensorProduct
  sensorReadings?: any[] // { metadata: any; readings: any } []

  viewport: Viewport

  currentTime: number
  userUnitSettings: UserUnitSettings

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
  showHiddenData?: boolean
  alertMarkers?: AlertMarker[]
}

export function FlowMeterChart({
  device,
  sigfoxId,
  isLoading,
  sensorPort,
  sensorReadings,
  sensorProduct,
  viewport,
  userUnitSettings,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = (timestamp: number) => {},
  showHiddenData = false,
  alertMarkers,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)

  const [showVolume, setShowVolume] = useState(false)
  const [showRate, setShowRate] = useState(true)
  const [showTotals, setShowTotals] = useState(true)

  // Register click listener ONCE when chart mounts (not on every hover)
  // Uses retry mechanism since CanvasJS renders the canvas asynchronously
  // NOTE: CanvasJS creates multiple canvas elements - we need the LAST one (topmost layer)
  useEffect(() => {
    if (!containerRef.current) return

    let canvas: HTMLCanvasElement | null = null
    let retryCount = 0
    const maxRetries = 20
    let timeoutId: ReturnType<typeof setTimeout>

    function onClickHandler() {
      addTimestamp(xRef.current)
    }

    function tryAttachListener() {
      const canvases = containerRef.current?.querySelectorAll('.canvasjs-chart-canvas')
      canvas = canvases && canvases.length > 0 ? (canvases[canvases.length - 1] as HTMLCanvasElement) : null
      if (canvas) {
        canvas.addEventListener('click', onClickHandler)
      } else if (retryCount < maxRetries) {
        retryCount++
        timeoutId = setTimeout(tryAttachListener, 100)
      }
    }

    tryAttachListener()

    return () => {
      clearTimeout(timeoutId)
      if (canvas) {
        canvas.removeEventListener('click', onClickHandler)
      }
    }
  }, [addTimestamp])

  // Default unit settings to prevent crash when userUnitSettings is not fully loaded
  const defaultUnit = { unitId: 1, precision: 2 }
  const settings = {
    volume: userUnitSettings[UnitCategoryId.Volume] ?? defaultUnit,
    volumeRate: userUnitSettings[UnitCategoryId.VolumeRate] ?? defaultUnit,
    totalVolume: userUnitSettings[UnitCategoryId.TotalVolume] ?? defaultUnit,
  }

  /**
   * Whether the account's unit preferences have arrived at all.
   *
   * This used to require Volume, VolumeRate AND TotalVolume to be present, and
   * rendered a spinner forever when any one was missing. That is not a loading
   * state: /user-unit-settings returns only the categories a user has actually
   * chosen for, so an account that never opened the units page was simply
   * absent a key and its flow charts span until the tab was closed — while
   * every non-flow chart on the same page drew normally.
   *
   * The server now fills missing categories with their base unit, so the
   * fallbacks above are a belt on top of braces. What matters here is only that
   * nothing has loaded yet, which is a genuine and temporary condition.
   */
  const settingsLoaded = Object.keys(userUnitSettings).length > 0

  // Check for readings - needs to be defined before the useEffect that uses it
  const hasReadings = checkForReadings()

  // Timestamp of the most recent reading, as a plain number so the query below
  // is keyed by value. Depending on `sensorReadings` directly would refetch on
  // every render of this chart's parent, which rebuilds the readings array each
  // time — this used to be a raw effect and did exactly that.
  const lastReadingTimestamp = useMemo(() => {
    const timestamps = Object.keys(sensorReadings?.[0]?.readings ?? {})
    return timestamps.length > 0 ? Math.floor(Number(timestamps[timestamps.length - 1])) : 0
  }, [sensorReadings])

  const { data: sensorConfigResponse } = useSensorConfigs(
    sigfoxId,
    lastReadingTimestamp,
    false,
    !!sigfoxId && lastReadingTimestamp > 0
  )

  const diameter = (sensorConfigResponse as any)?.data?.[sensorPort]?.conversionOffset || 0

  // Early return if settings not loaded to prevent crash in generateDataPoints
  // This must come AFTER all hooks
  if (!settingsLoaded) {
    return (
      <div className={tailwind.container}>
        <ChartLoadingPlaceholder />
      </div>
    )
  }

  let volumeConversions: any = null
  let volumeRateConversions: any = null

  let maxVolume = 0
  let maxRate = 0
  let maxTotals = 0
  const titleFontSize = 16
  const labelFontSize = 16
  const { dataPoints, totalVolume, totalVolumeInAcreFoot } = generateDataPoints()

  function generateDataPoints() {
    const dataPoints: DataPoints = {
      volume: [],
      totals: [],
      rate: [],
    }

    let volume = 0
    let totalVolume = 0
    let totalVolumeInAcreFoot = 0

    // add data points to get the full range of the viewport
    dataPoints.volume.push({ x: viewport.minimum, y: undefined })
    dataPoints.totals.push({ x: viewport.minimum, y: undefined })
    dataPoints.rate.push({ x: viewport.minimum, y: undefined })

    const volumeData =
      hasReadings && sensorReadings && sensorReadings.length > 0
        ? sensorReadings.find((reading: any) => reading?.metadata?.units?.category?.longName === 'Volume')
        : undefined

    if (volumeData) {
      const conversionFormulas = volumeData.metadata.units.conversions.formulas
      volumeConversions = volumeData.metadata.units.conversions.abbreviations


      // Get hidden map from sensor data for client-side filtering
      const volumeHiddenMap = volumeData.hidden || {}

      Object.keys(volumeData.readings)
        .filter(ts => !volumeHiddenMap[ts])  // Filter out hidden timestamps
        .forEach((ts: any) => {
          const timestamp = Number(ts) * 1000

          volume = convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[settings.volume.unitId])
          if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
            // Accumulate totalVolume using TotalVolume unit settings (e.g., acre-ft)
            totalVolume += convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[settings.totalVolume.unitId])
            if (totalVolume > maxTotals) maxTotals = totalVolume
            if (volume > maxVolume) maxVolume = volume

            dataPoints.volume.push({ x: timestamp, y: volume })
            dataPoints.totals.push({ x: timestamp, y: totalVolume })

            totalVolumeInAcreFoot += convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[UNIT_ID_ACRE_FOOT])
          }
        })
    }

    const volumeRateData =
      hasReadings && sensorReadings && sensorReadings.length > 0
        ? sensorReadings.find((reading: any) => reading?.metadata?.units?.category?.longName === 'Volume Rate')
        : undefined

    if (volumeRateData) {
      const conversionFormulas = volumeRateData.metadata.units.conversions.formulas
      volumeRateConversions = volumeRateData.metadata.units.conversions.abbreviations


      let lastTimestamp = 0

      // Get hidden map from sensor data for client-side filtering
      const rateHiddenMap = volumeRateData.hidden || {}

      Object.keys(volumeRateData.readings)
        .filter(ts => !rateHiddenMap[ts])  // Filter out hidden timestamps
        .forEach((ts: any) => {
          const timestamp = Number(ts) * 1000

          // this is to fix anomalies in the chart (when there is 20min gap between readings)
          if (timestamp - lastTimestamp > 20 * 60 * 1000) {
            dataPoints.rate.push({ x: timestamp - 60, y: undefined })
          }

          if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
            const rate = convertBaseToDisplay(volumeRateData.readings[ts], conversionFormulas[settings.volumeRate.unitId])

            if (rate > maxRate) maxRate = rate

            dataPoints.rate.push({ x: timestamp, y: rate })
          }

          lastTimestamp = timestamp
        })
    }

    dataPoints.volume.push({ x: viewport.maximum, y: undefined })
    dataPoints.totals.push({ x: viewport.maximum, y: undefined })
    dataPoints.rate.push({ x: viewport.maximum, y: undefined })

    return { dataPoints, totalVolume, totalVolumeInAcreFoot }
  }

  // Build full (unfiltered) data points for ghost overlay
  const ghostDataPoints = (() => {
    if (!showHiddenData || !sensorReadings || sensorReadings.length === 0) {
      return { volume: [], totals: [], rate: [] }
    }
    const result: DataPoints = { volume: [], totals: [], rate: [] }

    const volumeData = sensorReadings.find(
      (reading: any) => reading.metadata.units.category.longName === 'Volume'
    )
    if (volumeData) {
      const conversionFormulas = volumeData.metadata.units.conversions.formulas
      let ghostTotal = 0
      Object.keys(volumeData.readings)
        .forEach((ts: any) => {
          const timestamp = Number(ts) * 1000
          const vol = convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[settings.volume.unitId])
          ghostTotal += convertBaseToDisplay(volumeData.readings[ts], conversionFormulas[settings.totalVolume.unitId])
          if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
            result.volume.push({ x: timestamp, y: vol })
            result.totals.push({ x: timestamp, y: ghostTotal })
          }
        })
    }

    const volumeRateData = sensorReadings.find(
      (reading: any) => reading.metadata.units.category.longName === 'Volume Rate'
    )
    if (volumeRateData) {
      const conversionFormulas = volumeRateData.metadata.units.conversions.formulas
      Object.keys(volumeRateData.readings)
        .forEach((ts: any) => {
          const timestamp = Number(ts) * 1000
          if (timestamp >= viewport.minimum && timestamp <= viewport.maximum) {
            const rate = convertBaseToDisplay(volumeRateData.readings[ts], conversionFormulas[settings.volumeRate.unitId])
            result.rate.push({ x: timestamp, y: rate })
          }
        })
    }
    return result
  })()

  const hiddenStripLines = (() => {
    if (!showHiddenData || !sensorReadings || sensorReadings.length === 0) return []
    const volumeData = sensorReadings.find(
      (reading: any) => reading.metadata.units.category.longName === 'Volume'
    )
    return volumeData ? getHiddenRangeStripLines(volumeData.hidden || {}) : []
  })()

  const flowVolume = {
    name: 'Volume',
    type: 'column',
    axisYType: 'secondary',
    axisYIndex: 1,
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'blue',
    visible: showVolume,
    fillOpacity: 0.4,
    dataPoints: showVolume ? dataPoints.volume : [{ x: 0, y: 0 }],
  }

  const flowRate = {
    name: 'Flow Rate',
    axisYType: 'primary',
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'red',
    visible: showRate,
    fillOpacity: 0.1,
    dataPoints: showRate ? dataPoints.rate : [{ x: 0, y: 0 }],
  }

  const flowTotals = {
    name: 'Flow Total',
    axisYType: 'secondary',
    axisYIndex: 0,
    type: 'area',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'lightgreen',
    visible: showTotals,
    fillOpacity: 0.4,
    dataPoints: showTotals ? dataPoints.totals : [{ x: 0, y: 0 }],
  }

  const maxVolumeRateTotal = Math.max(showRate ? maxRate : 0) || 10

  // The Volume (gal) axis is shown when the Volume series is toggled on, or when
  // the "show hidden data" ghost overlay renders Volume (hidden) bars against it.
  const showVolumeAxis = showVolume || showHiddenData

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_FlowMeter`,
    title: {
      text: 'Flow Meter',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    willReadFrequently: true,
    dataPointWidth: 4,
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          if (e.entries[i].dataSeries.name.includes('(hidden)')) continue
          const name = e.entries[i].dataSeries.name
          const isRate = name === 'Flow Rate'
          const isTotal = name === 'Flow Total'
          const precision = isRate
            ? settings.volumeRate.precision
            : isTotal
            ? settings.totalVolume.precision
            : settings.volume.precision
          const value = e.entries[i].dataPoint.y
          const color = e.entries[i].dataSeries.color
          const units = isRate
            ? getUnitAbbreviation(volumeRateConversions, settings.volumeRate.unitId, 'gpm')
            : isTotal
            ? getUnitAbbreviation(volumeConversions, settings.totalVolume.unitId, 'acre-ft')
            : getUnitAbbreviation(volumeConversions, settings.volume.unitId, 'gal')

          content += `<span style="color:${color}">${name}:</span> <strong> ${
            value !== undefined ? formatNumber(value, precision) : ''
          } ${value !== undefined ? units : ''}</strong>`

          // Add velocity (ft/s) for Flow Rate
          if (name === 'Flow Rate' && value !== undefined && diameter > 0) {
            const velocity = (value * 0.002228) / (Math.PI * Math.pow(diameter / 24, 2))
            content += ` <span style="color:blue">(${formatNumber(velocity, 1)} ft/s)</span>`
          }

          content += '<br/>'
        }

        xRef.current = e.entries[0].dataPoint.x

        return content
      },
    },

    // subtitles: [
    //   {
    //     text: isLoading ? null : hasReadings ? null : 'RIC has no data for this time period',
    //     backgroundColor: 'red',
    //     fontColor: 'white',
    //     //Uncomment properties below to see how they behave
    //     margin: 10,
    //     padding: 15,
    //     cornerRadius: 5,
    //     fontSize: 20,
    //     fontWeight: 'bold',
    //     verticalAlign: 'top',
    //     dockInsidePlotArea: true,
    //   },
    // ],

    axisX: [
      {
        labelFormatter: createAxisLabelFormatter(),
        viewportMinimum: viewport?.minimum || null,
        viewportMaximum: viewport?.maximum || null,
        // labelAngle: -90,
        stripLines: [
          ...selectXRange.map((value) => {
            return {
              value,
              color: 'red',
              labelPlacement: 'outside',
            }
          }),

          selectXRange.length === 2 && {
            startValue: selectXRange[0],
            endValue: selectXRange[1],
            color: '#d8d8d8',
          },

          ...hiddenStripLines,
          ...alertMarkersToStripLines(alertMarkers),
        ],
      },
    ],
    axisY: [
      {
        includeZero: false,
        title: `Flow Rate (${getUnitAbbreviation(volumeRateConversions, settings.volumeRate.unitId, 'gpm')})`,
        suffix: '',
        minimum: 0,
        maximum: maxVolumeRateTotal * 2.5,
        titleFontSize,
        labelFontSize,
        dataPointWidth: 500,
      },
    ],
    axisY2: [
      {
        // Index 0: cumulative Flow Total, scaled to the totals magnitude (e.g. acre-ft)
        includeZero: false,
        title: `Flow Total (${getUnitAbbreviation(volumeConversions, settings.totalVolume.unitId, 'acre-ft')})`,
        suffix: '',
        minimum: 0,
        maximum: (showTotals ? maxTotals : 10) * 1.3,
        titleFontSize,
        labelFontSize,
        margin: 10,
        dataPointWidth: 500,
      },
      {
        // Index 1: per-reading Volume bars, scaled to the per-reading magnitude (e.g. gal).
        // Kept in the array so axisYIndex:1 stays stable, but collapsed to invisible when
        // no volume series is on-screen (the default) so it doesn't clutter the chart.
        // Shown when the Volume series is toggled on OR the ghost "show hidden data"
        // overlay is rendering Volume (hidden) bars against this axis.
        includeZero: false,
        title: showVolumeAxis ? `Volume (${getUnitAbbreviation(volumeConversions, settings.volume.unitId, 'gal')})` : '',
        suffix: '',
        minimum: 0,
        maximum: (showVolumeAxis ? maxVolume : 10) * 1.3,
        titleFontSize,
        labelFontSize,
        margin: 10,
        dataPointWidth: 500,
        ...(showVolumeAxis
          ? {}
          : { labelFormatter: () => '', lineThickness: 0, tickLength: 0, gridThickness: 0 }),
      },
    ],
    data: [
      ...(showHiddenData && ghostDataPoints.volume.length > 0
        ? [
            {
              name: 'Flow Rate (hidden)',
              axisYType: 'primary',
              type: 'line',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              lineThickness: 2,
              markerSize: 0,
              dataPoints: ghostDataPoints.rate,
              toolTipContent: null,
            },
            {
              name: 'Volume (hidden)',
              type: 'column',
              axisYType: 'secondary',
              axisYIndex: 1,
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.volume,
              toolTipContent: null,
            },
            {
              name: 'Flow Total (hidden)',
              axisYType: 'secondary',
              axisYIndex: 0,
              type: 'area',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.totals,
              toolTipContent: null,
            },
          ]
        : []),
      flowRate,
      flowVolume,
      flowTotals,
    ],
    legend: {
      itemclick: (e: any) => {
        switch (e.dataSeries.name) {
          case 'Volume':
            setShowVolume(!showVolume)
            break
          case 'Flow Rate':
            setShowRate(!showRate)
            break
          case 'Flow Total':
            setShowTotals(!showTotals)
            break
        }
      },
    },
  }

  function checkForReadings() {
    return (
      sensorReadings &&
      Object.values(sensorReadings).filter(
        (data: any) => {
          const hiddenMap = data.hidden || {}
          return Object.keys(data.readings).filter((timestamp) => {
            if (hiddenMap[timestamp]) return false  // Skip hidden readings
            const ts = Number(timestamp) * 1000
            return ts >= viewport.minimum && ts <= viewport.maximum
          }).length > 0
        }
      ).length > 0
    )
  }

  function validateSensorMetadata(): boolean {
    if (!sensorReadings || sensorReadings.length === 0) {
      return false
    }

    const volumeData = sensorReadings.find(
      (r: any) => r.metadata?.units?.category?.longName === 'Volume'
    )
    const volumeRateData = sensorReadings.find(
      (r: any) => r.metadata?.units?.category?.longName === 'Volume Rate'
    )

    if (!volumeData || !volumeRateData) {
      console.error('❌ Missing sensor data:', {
        hasVolume: !!volumeData,
        hasVolumeRate: !!volumeRateData,
        sensors: sensorReadings.map((r: any) => r.metadata?.units?.category?.longName)
      })
      return false
    }

    if (!volumeData.metadata?.units?.conversions?.abbreviations) {
      console.error('❌ Volume sensor missing conversions.abbreviations')
      return false
    }

    if (!volumeRateData.metadata?.units?.conversions?.abbreviations) {
      console.error('❌ Volume Rate sensor missing conversions.abbreviations')
      return false
    }

    return true
  }

  function getLastReadingTimestamp(): number {
    if (!hasReadings || !sensorReadings) return 0
    const timestamps = Object.keys(sensorReadings[0].readings)
    const ts = Number(timestamps[timestamps.length - 1])
    return ts
  }

  const currentRate = dataPoints.rate?.filter((data) => data.y !== undefined).slice(-1)[0]?.y

  // const title = `${device.config.device.name}: ${device.config.site.name || '-'}`
  const title = 'Flow Meter'

  // Guard against missing device
  if (!device) {
    return null
  }

  return (
    <div className={tailwind.container}>
      <CollapsibleSection
        title={title}
        // mode={mode}
        // setMode={setMode}
        open={true}
        isLoading={isLoading}
      >
        <div className={tailwind.collapsibleSection}>
          <FlowMeterChartSubHeader
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorProduct={sensorProduct}
            sensorMetadata={sensorReadings ? sensorReadings.map((data: any) => data.metadata) : []}
            currentRate={currentRate || 0}
            totalVolume={totalVolume || 0}
            totalVolumeInAcreFoot={totalVolumeInAcreFoot || 0}
            lastReading={getLastReadingTimestamp()}
            settings={settings}
          />
        </div>
      </CollapsibleSection>
      {isLoading ? (
        <ChartLoadingPlaceholder />
      ) : hasReadings && !validateSensorMetadata() ? (
        <div className="flex items-center justify-center py-8">
          <div className="bg-yellow-500 text-white font-bold rounded px-6 py-3 text-center">
            Chart metadata incomplete. Please clear browser cache and refresh.
          </div>
        </div>
      ) : hasReadings ? (
        <div ref={containerRef}>
          <CanvasJSChart options={options} />
        </div>
      ) : (
        <div className="flex items-center justify-center py-8">
          <div className="bg-red-500 text-white font-bold rounded px-6 py-3 text-center">
            This RIC has no data for the selected period
          </div>
        </div>
      )}
    </div>
  )
}

const tailwind = {
  container: 'mb-4',
  collapsibleSection: 'pb-2',
}
